/* eslint-disable no-underscore-dangle */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import type { StoreLocatorLayoutSlot as StoreLocatorLayoutSlotSchema } from '@amplience/content-types/typings/s-store-locator-layout';
import { getStoreLocatorBaseUrl } from '@storefront/lib-global';
import { useLocaleContext } from '@storefront/lib-global/context/localeContext';
import { useStaticContext } from '@storefront/lib-global/context/staticContext';
import { Loader } from '@storefront/lib-global/globalComponents/Loader/Loader';
import { LocationComponent } from '@storefront/lib-global/globalComponents/locationComponent';
import type {
  LocationComponentHandle,
  LocationDataType,
  LocationErrorType,
} from '@storefront/lib-global/globalComponents/locationComponent';
import type { StoreMapMarker } from '@storefront/lib-global/globalComponents/maps/StoreMap';
import { StoreMap } from '@storefront/lib-global/globalComponents/maps/StoreMap';
import { useLoadingCursor } from '@storefront/lib-global/hooks/useLoadingCursor';
import { useMediaMatch } from '@storefront/lib-global/hooks/useMediaMatch';
import { useStar2 } from '@storefront/lib-global/hooks/useStar2';
import { ArrowDownSmall } from '@storefront/lib-global/icons';
import type { AlgoliaStoreType } from '@storefront/lib-global/services/algolia/getStores';
import { getStoresAroundRadius } from '@storefront/lib-global/services/algolia/getStores';
import { TypographyStyles, media } from '@storefront/lib-global/stylings';
import { ExcludedStores, StoreDetailType } from '@storefront/lib-global/types/store';
import type { LatLng } from '@storefront/lib-global/types/store';
import { countryCodeMapperForStoreLocator } from '@storefront/lib-global/utils/internationalization/localeHelper';
import { searchRadiusByLocale } from '@storefront/lib-global/utils/mapUtils';
import { getItem, setItem } from '@storefront/lib-global/utils/storage';
import { getLocalizedValue } from '@storefront/lib-global/utils/transformers';
import { useGetLocation, useGetPagedStoreLocations, useStoresLocationInfo } from '../../hooks';
import type { BreadcrumbDataType } from '../contentBreadcrumb';
import { ContentBreadcrumb } from '../contentBreadcrumb';
import { StoreLocatorItem } from './StoreLocatorItem';
import {
  FindButton,
  FocusedMapViewStoreItem,
  LoadMoreButton,
  LocatorContent,
  NoResultsMessage,
  ResultsContent,
  ResultsList,
  ResultsTabContainer,
  StoreCardMap,
  StoreCardMapContent,
  StoreListSection,
  StoreLocatorContent,
  StoreLocatorContentBodyText,
  StoreLocatorHeader,
  StoreLocatorMobileContentBodyText,
  StoreSearchContent,
  TabSwitcher,
  TabSwitcherButton,
} from './StoreLocatorPageLayout.styles';

export type StoreStockErrors = {
  location?: LocationErrorType;
};

export type StoreLocatorPageProps = {
  storeLocatorLayout: StoreLocatorLayoutSlotSchema;
  breadcrumbData: BreadcrumbDataType[];
};

export function StoreLocatorPageLayout({ storeLocatorLayout, breadcrumbData }: StoreLocatorPageProps) {
  const { locale } = useLocaleContext();
  const { t } = useTranslation(['lib-global-common']);

  const isDesktop = useMediaMatch(media.greaterThan('lg'));

  const { errors, setErrors, isLoading, setIsLoading, showMessage, setShowMessage } = useGetLocation();
  const { searchLocation, setSearchLocation, storesData, setStoresData, selectedStore, setSelectedStore } =
    useStoresLocationInfo();
  const {
    hasLoadMore,
    setHasLoadMore,
    pagedStoresItems,
    setPagedStoresItems,
    pagedStoreMarkers,
    setPagedStoreMarkers,
  } = useGetPagedStoreLocations();
  const { previousURL } = useStaticContext();
  const { isStar2 } = useStar2();

  const storeLocationRef = useRef<LocationComponentHandle>(null);
  const [storesMarkerData, setStoresMarkerData] = useState<StoreMapMarker[]>([]);
  const [activeTab, setActiveTab] = useState('listView');
  const [firstLoad, setFirstLoad] = useState(true);
  const [zoomedStore, setZoomedStore] = useState('');
  const storeLocatorUrl = getStoreLocatorBaseUrl(locale);
  const [initialLocation, setInitialLocation] = useState(
    () =>
      (previousURL.includes(`/${storeLocatorUrl}/`) && getItem('storeLocatorQuery', true, 'sessionStorage')) ||
      undefined,
  );
  const { storeLocatorContentBody } = storeLocatorLayout || {};

  useLoadingCursor(isLoading);

  const fetchStoresData = useCallback(async () => {
    let coordinates: LocationDataType | undefined;
    setIsLoading(true);

    if (initialLocation) {
      coordinates = initialLocation;
      setInitialLocation(undefined);
    } else {
      coordinates = await storeLocationRef?.current?.getLocation();
    }

    if (coordinates) {
      setSearchLocation(coordinates);
      const data = await getStoresAroundRadius(coordinates.lat, coordinates.lng, searchRadiusByLocale[locale], 10);
      const filteredByLocaleData =
        data?.filter(({ address }) => address?.country === countryCodeMapperForStoreLocator[locale]) || [];

      const filteredStoresData = filteredByLocaleData.filter(
        (item) =>
          !Object.values(ExcludedStores).some(
            (excludedName) => item.displayName?.toLowerCase() === excludedName.toLowerCase(),
            // Filter out stores with unknown store type (business decision). These stores are used for internal purposes
          ) && item.storeType !== StoreDetailType.UNKNOWN,
      );

      setShowMessage(filteredStoresData.length === 0);

      const transformedStoreArray = filteredStoresData.map((store, i) => ({
        lat: store._geoloc?.lat,
        lng: store._geoloc?.lng,
        mapLabel: (i + 1).toString().padStart(2, '0'),
        zIndex: 10 - i,
        id: store.objectID,
        icon: undefined,
      }));

      setStoresMarkerData(transformedStoreArray as unknown as StoreMapMarker[]);
      setStoresData(filteredStoresData);
      setPagedStoreMarkers(transformedStoreArray.slice(0, 5) as unknown as StoreMapMarker[]);
      setPagedStoresItems(filteredStoresData.slice(0, 5));
      setSelectedStore(filteredStoresData[0]?.objectID);

      if (coordinates.locationQuery !== getItem('storeLocatorQuery')?.locationQuery) {
        setItem(
          'storeLocatorQuery',
          coordinates.locationQuery ? coordinates : { ...coordinates, ...{ locationQuery: 'Current Location' } },
          true,
          'sessionStorage',
        );
      }
    }

    setIsLoading(false);
    setFirstLoad(false);
  }, [
    initialLocation,
    locale,
    setIsLoading,
    setPagedStoreMarkers,
    setPagedStoresItems,
    setSearchLocation,
    setSelectedStore,
    setShowMessage,
    setStoresData,
  ]);

  const focusedMapViewStoreInfo = useMemo(
    () => storesData.find((s) => s.objectID === selectedStore),
    [selectedStore, storesData],
  );

  const zoomedStoreInfo = useMemo(() => storesData.find((s) => s.objectID === zoomedStore), [zoomedStore, storesData]);

  const loadMoreStores = async () => {
    const pagedItems = storesData.slice(0, pagedStoresItems.length + 5);
    const pagedMapMarkers = storesMarkerData.slice(0, pagedStoreMarkers.length + 5);
    setPagedStoresItems(pagedItems);
    setPagedStoreMarkers(pagedMapMarkers);
    setHasLoadMore(storesData.length > pagedItems.length);
  };

  const handleTabSwitch = (tab: string) => () => {
    setActiveTab(tab);
  };

  useEffect(() => {
    setHasLoadMore(storesData.length > 5);
  }, [storesData, setHasLoadMore]);

  useEffect(() => {
    if (!initialLocation) return;

    (async () => {
      await fetchStoresData();
    })();

    storeLocationRef?.current?.blur();
  }, [fetchStoresData, initialLocation]);

  return (
    <StoreLocatorContent>
      {breadcrumbData?.length ? <ContentBreadcrumb breadcrumbData={breadcrumbData} /> : null}
      <StoreLocatorHeader>{t('store.locator.header')}</StoreLocatorHeader>
      <LocatorContent>
        <StoreListSection>
          <StoreSearchContent>
            <LocationComponent
              ref={storeLocationRef}
              onEnterLocation={fetchStoresData}
              errors={errors}
              setErrors={setErrors}
              initialLocation={initialLocation}
              restrictToLocale
            />
            <FindButton
              type="button"
              onClick={fetchStoresData}
              disabled={errors.location?.error}
              data-testid="findStoreButton"
            >
              {isLoading ? <Loader /> : t('store.locator.cta')}
            </FindButton>
          </StoreSearchContent>
          <ResultsContent>
            {!firstLoad && (
              <ResultsTabContainer data-testid="storeResultsCount">
                {!isDesktop && (
                  <TabSwitcher>
                    <TabSwitcherButton
                      tabIndex={0}
                      $isActive={activeTab === 'listView'}
                      onClick={handleTabSwitch('listView')}
                    >
                      {t('stores.list.view')}
                    </TabSwitcherButton>
                    <TabSwitcherButton
                      tabIndex={0}
                      $isActive={activeTab === 'mapView'}
                      onClick={handleTabSwitch('mapView')}
                    >
                      {t('stores.map.view')}
                    </TabSwitcherButton>
                  </TabSwitcher>
                )}
                {!isLoading && storesData.length > 0 && (
                  <span>{t('store.locator.result.count', { count: storesData.length })}</span>
                )}
              </ResultsTabContainer>
            )}
            {!isLoading ? (
              <>
                {isDesktop && showMessage && <NoResultsMessage>{t('map.no.results.found')}</NoResultsMessage>}
                {pagedStoresItems.length > 0 && (
                  <ResultsList $isListViewActive={activeTab === 'listView'}>
                    {pagedStoresItems.map((item) => (
                      <StoreLocatorItem
                        key={item.objectID}
                        isMultipleTags={!!item.features?.length && item.features.length > 1}
                        isScheduleGrouped
                        isActiveStore={selectedStore === item.objectID}
                        setActiveStoreId={setSelectedStore}
                        id={item.objectID}
                        distance={item._rankingInfo?.matchedGeoLocation?.distance || 0}
                        features={item.features}
                        setZoomedStore={setZoomedStore}
                        showOpeningHours={
                          !isStar2 || (item.storeType !== StoreDetailType.STOCKIST && !!item?.openingHours?.length)
                        }
                        {...(item as AlgoliaStoreType)}
                      />
                    ))}
                  </ResultsList>
                )}
                {hasLoadMore && (isDesktop || activeTab === 'listView') && (
                  <LoadMoreButton type="button" onClick={loadMoreStores} data-testid="loadMoreButton">
                    {[<ArrowDownSmall key="store-locator-load-more-arrow" />, t('list.store.load.more')]}
                  </LoadMoreButton>
                )}
              </>
            ) : null}
          </ResultsContent>
          {!isDesktop && activeTab === 'listView' && showMessage && (
            <NoResultsMessage>{t('map.no.results.found')}</NoResultsMessage>
          )}
        </StoreListSection>
        <StoreCardMapContent $isMapViewActive={activeTab === 'mapView'}>
          <StoreCardMap>
            <StoreMap
              fitType={searchLocation ? 'markers' : 'center-point'}
              defaultLocation={searchLocation}
              height={isDesktop ? '744px' : '285px'}
              stores={pagedStoreMarkers}
              onStoreSelect={setSelectedStore}
              selectedStoreId={selectedStore}
              zoomedMarkerLocation={zoomedStoreInfo?._geoloc as unknown as LatLng}
            />
          </StoreCardMap>
          {!isDesktop && focusedMapViewStoreInfo && activeTab === 'mapView' && (
            <FocusedMapViewStoreItem>
              <StoreLocatorItem
                isMultipleTags={
                  !!focusedMapViewStoreInfo?.features?.length && focusedMapViewStoreInfo.features.length > 1
                }
                isScheduleGrouped
                isActiveStore={selectedStore === focusedMapViewStoreInfo?.objectID}
                id={focusedMapViewStoreInfo?.objectID || ''}
                distance={focusedMapViewStoreInfo?._rankingInfo?.matchedGeoLocation?.distance || 0}
                features={focusedMapViewStoreInfo?.features}
                setZoomedStore={setZoomedStore}
                {...(focusedMapViewStoreInfo as AlgoliaStoreType)}
              />
            </FocusedMapViewStoreItem>
          )}
          {!isDesktop && showMessage && (
            <NoResultsMessage $isMapViewActive={activeTab === 'mapView'}>{t('map.no.results.found')}</NoResultsMessage>
          )}
          {storeLocatorContentBody && (
            <StoreLocatorContentBodyText
              text={getLocalizedValue(storeLocatorContentBody.richText)}
              fontSize={TypographyStyles.Body.Small.Regular}
            />
          )}
        </StoreCardMapContent>
        {storeLocatorContentBody && (
          <StoreLocatorMobileContentBodyText
            text={getLocalizedValue(storeLocatorContentBody.richText)}
            fontSize={TypographyStyles.Body.Small.Regular}
          />
        )}
        <span className="visually-hidden" role="status">
          {storesData.length > 0
            ? t('store.locator.result.count', { count: storesData.length })
            : t('map.no.results.found')}
        </span>
      </LocatorContent>
    </StoreLocatorContent>
  );
}

export default StoreLocatorPageLayout;
